import React, { useEffect, useCallback } from 'react';

import {
  CoveredPartyPayloadType,
  CalculateDriverStatusResponse,
} from '@clearcover/core-app-types';

import {
  createDriverStatusYupValidation,
  makeCalculateDriverStatusPayload,
} from './utils';

type CalculateDriverStatusType = (payload: {
  data: any;
}) => Promise<{ data: { driverStatus: any } }>;

interface DriverStatusValidationProps {
  values: CoveredPartyPayloadType;
  calculateDriverStatus: CalculateDriverStatusType;
  updateDriverStatus: (status: any) => void;
  driverOptionsQuestionsConfig: {
    askLivesInHousehold: boolean;
    askHasAccessToVehicles: boolean;
    askHasOwnInsurancePolicy: boolean;
    askForUserReportedLicenseStatus: boolean;
  };
  currentDriverStatus?: CalculateDriverStatusResponse['driverStatus'];
}

export const DriverStatusValidation: React.FC<DriverStatusValidationProps> = ({
  values,
  calculateDriverStatus,
  updateDriverStatus,
  driverOptionsQuestionsConfig,
  currentDriverStatus,
}) => {
  const validateDriverStatusData = useCallback(
    async (values: CoveredPartyPayloadType) => {
      const DRIVER_STATUS_VALIDATION = createDriverStatusYupValidation(
        driverOptionsQuestionsConfig
      );

      try {
        await DRIVER_STATUS_VALIDATION.validate(values);
        calculateDriverStatus({
          data: makeCalculateDriverStatusPayload(values),
        })
          .then(response => {
            updateDriverStatus(response.data.driverStatus);
          })
          .catch(() => {
            updateDriverStatus(undefined);
          });
      } catch (error) {}
    },
    [calculateDriverStatus, updateDriverStatus, driverOptionsQuestionsConfig]
  );

  useEffect(() => {
    validateDriverStatusData(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // this should only run when the following values change, because these fields are used to calculate driver status
    // if a new field is added to the calculation, it should be added here
    values.info.licenseType,
    values.info.licenseStatus,
    values.info.licenseState,
    values.info.licenseNumber,
    values.info.exposureDetails?.hasAccessToVehicles,
    values.info.exposureDetails?.hasOwnAutoPolicy,
    values.info.exposureDetails?.livesWithPolicyholder,
  ]);

  useEffect(() => {
    if (values.policyPartyRelationship !== currentDriverStatus) {
      updateDriverStatus(values.policyPartyRelationship);
    }

    // this should only run when the following value change because that is when excluded driver checkbox is toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.policyPartyRelationship]);

  return null;
};
